import { useEffect, useState } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchDrivers, createDriver, updateDriver } from "@/services/superadminApi";
import Loader from "@/components/ui/Loader";
import {
    SectionHeader,
    Badge,
    CardGlass,
    Button,
    StatCard
} from "@/components/ui/PremiumComponents";
import { Users, Truck, Clock, MapPin, ChevronRight, Search, Mail, ExternalLink, Plus, Edit2 } from 'lucide-react';

export default function SuperAdminDrivers() {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ name: "", phone: "", password: "", scope: "network", vehicle: "motorcycle", email: "", allowed_company_ids: "" });
    const [creating, setCreating] = useState(false);
    
    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editDriver, setEditDriver] = useState(null);
    const [editFormData, setEditFormData] = useState({ scope: "network", allowed_company_ids: "", active: true });
    const [updating, setUpdating] = useState(false);

    const loadDrivers = () => {
        setLoading(true);
        fetchDrivers()
            .then(setDrivers)
            .catch(() => setError("Error al sincronizar la red de repartidores."))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadDrivers();
    }, []);

    const handleCreateDriver = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const dataToSend = { ...formData };
            if (formData.scope === "network" && formData.allowed_company_ids) {
                dataToSend.allowed_company_ids = formData.allowed_company_ids.split(",").map(s => s.trim()).filter(s => s);
            }
            await createDriver(dataToSend);
            setIsModalOpen(false);
            setFormData({ name: "", phone: "", password: "", scope: "network", vehicle: "motorcycle", email: "", allowed_company_ids: "" });
            loadDrivers();
        } catch (err) {
            alert("Error al crear repartidor: " + (err.response?.data?.detail || err.message));
        } finally {
            setCreating(false);
        }
    };

    const handleEditClick = (driver) => {
        setEditDriver(driver);
        setEditFormData({
            scope: driver.scope || "network",
            allowed_company_ids: driver.allowed_company_ids ? driver.allowed_company_ids.join(", ") : "",
            active: driver.active !== false
        });
        setIsEditModalOpen(true);
    };

    const handleUpdateDriver = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            const dataToSend = { ...editFormData };
            if (editFormData.scope === "network" && editFormData.allowed_company_ids) {
                dataToSend.allowed_company_ids = editFormData.allowed_company_ids.split(",").map(s => s.trim()).filter(s => s);
            } else if (editFormData.scope === "restaurant") {
                dataToSend.allowed_company_ids = [];
            }
            await updateDriver(editDriver.driver_id, dataToSend);
            setIsEditModalOpen(false);
            loadDrivers();
        } catch (err) {
            alert("Error al actualizar: " + (err.response?.data?.detail || err.message));
        } finally {
            setUpdating(false);
        }
    };

    const kpis = [
        { label: "Total Flota", value: drivers.length, delta: 8, trend: 'up', icon: <Users />, color: 'var(--primary)' },
        { label: "En Servicio", value: drivers.filter(d => d.shift_status === 'active').length, delta: 5, trend: 'up', icon: <Truck />, color: 'var(--success)' },
        { label: "Tiempo Medio", value: "28m", delta: 15, trend: 'down', icon: <Clock />, color: 'var(--secondary)' },
        { label: "Zonas Activas", value: "14", delta: 2, trend: 'up', icon: <MapPin />, color: 'var(--accent)' },
    ];

    return (
        <SuperAdminLayout>
            <div className="page-fade-in">
                <SectionHeader
                    title="Repartidores Globales"
                    subtitle="Gestión y monitoreo de la flota logística activa en la red de Conexo POS."
                    actions={
                        <div className="flex gap-4">
                            <Button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2" variant="primary">
                                <Plus size={16} /> Nuevo Driver
                            </Button>
                            <Button className="flex items-center gap-2" variant="secondary">
                                <ExternalLink size={16} /> Mapa en Vivo
                            </Button>
                        </div>
                    }
                />

                {/* KPI Strip */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    {kpis.map((kpi, i) => (
                        <StatCard key={i} {...kpi} />
                    ))}
                </div>

                {error && <div className="bg-danger/10 border border-danger/20 p-4 rounded-xl text-danger mb-8 font-bold">⚠️ {error}</div>}

                <div className="flex flex-col gap-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center mb-6">
                        <div className="relative flex-1 w-full group">
                            <Search
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-primary transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                            />
                            <input
                                type="text"
                                placeholder="Buscar por nombre, email o restaurante..."
                                className="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-white outline-none focus:ring-2 ring-primary/20 transition-all font-medium"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3].map(i => (
                                <CardGlass key={i} className="animate-pulse-slow h-64" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {drivers.map((d) => (
                                <CardGlass
                                    key={d.user_id}
                                    className="flex flex-col justify-between"
                                    headerAction={<Badge status={d.shift_status === 'active' ? 'success' : 'default'}>{d.shift_status === 'active' ? 'EN TURNO' : 'INACTIVO'}</Badge>}
                                >
                                    <div className="flex gap-4 items-center mb-6">
                                        <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl">
                                            <span>🛵</span>
                                        </div>
                                        <div className="flex-1 overflow-hidden">
                                            <h3 className="text-lg font-bold text-white tracking-tight truncate">{d.name}</h3>
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest mt-1" style={{ color: 'var(--text-muted)' }}>
                                                <Mail size={12} className="text-primary" />
                                                <span className="truncate">{d.email}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-[#020617]/40 rounded-2xl p-4 space-y-3 mb-6 border border-white/5">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Restaurante</span>
                                            <span className="text-white font-bold">{d.company_name}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Entregas</span>
                                            <span className="text-white font-bold">{d.delivered_count}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="font-black uppercase tracking-wider" style={{ color: 'var(--text-soft)' }}>Rendimiento</span>
                                            <Badge status="info">{d.avg_delivery_time_mins} MIN AVG</Badge>
                                        </div>
                                    </div>

                                    <Button variant="secondary" className="w-full flex items-center justify-center gap-2" onClick={() => handleEditClick(d)}>
                                        <Edit2 size={14} /> Editar Configuración
                                    </Button>
                                </CardGlass>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Crear Repartidor */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <CardGlass className="w-full max-w-md" title="Nuevo Repartidor" subtitle="Añadir a la red global o restaurante.">
                        <form onSubmit={handleCreateDriver} className="flex flex-col gap-4 mt-6">
                            <input
                                required
                                type="text"
                                placeholder="Nombre completo"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                            />
                            <input
                                required
                                type="tel"
                                placeholder="Teléfono"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                value={formData.phone}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                            />
                            <input
                                type="email"
                                placeholder="Email (opcional)"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                            />
                            <input
                                required
                                type="password"
                                placeholder="Contraseña de acceso"
                                className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                value={formData.password}
                                onChange={e => setFormData({ ...formData, password: e.target.value })}
                            />
                            <select
                                className="w-full bg-slate-900 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                value={formData.scope}
                                onChange={e => setFormData({ ...formData, scope: e.target.value })}
                            >
                                <option value="network">Red Global (Network)</option>
                                <option value="restaurant">Solo un Restaurante</option>
                            </select>
                            <select
                                className="w-full bg-slate-900 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                value={formData.vehicle}
                                onChange={e => setFormData({ ...formData, vehicle: e.target.value })}
                            >
                                <option value="motorcycle">Moto</option>
                                <option value="bicycle">Bici</option>
                                <option value="car">Coche</option>
                            </select>
                            
                            {formData.scope === "network" && (
                                <div>
                                    <label className="text-xs font-black uppercase text-white/50 mb-2 block">
                                        ID Restaurantes permitidos
                                        <span className="text-white/30 text-[10px] ml-2">(opcional, separados por coma)</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="company_id_1, company_id_2, ... (dejar vacío para todos)"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                        value={formData.allowed_company_ids}
                                        onChange={e => setFormData({ ...formData, allowed_company_ids: e.target.value })}
                                    />
                                </div>
                            )}

                            <div className="flex gap-4 mt-4">
                                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                                <Button type="submit" variant="primary" className="flex-1" disabled={creating}>
                                    {creating ? 'Creando...' : 'Crear Repartidor'}
                                </Button>
                            </div>
                        </form>
                    </CardGlass>
                </div>
            )}

            {/* Modal Editar Repartidor */}
            {isEditModalOpen && editDriver && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <CardGlass className="w-full max-w-md" title="Editar Repartidor" subtitle={`Editando: ${editDriver.name}`}>
                        <form onSubmit={handleUpdateDriver} className="flex flex-col gap-4 mt-6">
                            <div>
                                <label className="text-xs font-black uppercase text-white/50 mb-2 block">Alcance</label>
                                <select
                                    className="w-full bg-slate-900 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                    value={editFormData.scope}
                                    onChange={e => setEditFormData({ ...editFormData, scope: e.target.value })}
                                >
                                    <option value="network">Red Global (todos los restaurantes)</option>
                                    <option value="restaurant">Solo un Restaurante</option>
                                </select>
                            </div>
                            
                            {editFormData.scope === "network" && (
                                <div>
                                    <label className="text-xs font-black uppercase text-white/50 mb-2 block">
                                        ID Restaurantes permitidos
                                        <span className="text-white/30 text-[10px] ml-2">(separados por coma)</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="company_id_1, company_id_2, ..."
                                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white outline-none focus:ring-2 ring-primary/20"
                                        value={editFormData.allowed_company_ids}
                                        onChange={e => setEditFormData({ ...editFormData, allowed_company_ids: e.target.value })}
                                    />
                                    <p className="text-[10px] text-white/30 mt-1">
                                        Ingresa los company_id separados por coma. Deja vacío para permitir TODOS los restaurantes.
                                    </p>
                                </div>
                            )}

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="active"
                                    checked={editFormData.active}
                                    onChange={e => setEditFormData({ ...editFormData, active: e.target.checked })}
                                    className="w-5 h-5 rounded"
                                />
                                <label htmlFor="active" className="text-white font-bold">Activo</label>
                            </div>

                            <div className="flex gap-4 mt-4">
                                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsEditModalOpen(false)}>Cancelar</Button>
                                <Button type="submit" variant="primary" className="flex-1" disabled={updating}>
                                    {updating ? 'Guardando...' : 'Guardar Cambios'}
                                </Button>
                            </div>
                        </form>
                    </CardGlass>
                </div>
            )}
        </SuperAdminLayout>
    );
}
